<template>
    <div class="agreement-detail">
        <div v-if="fetching" class="agreement-detail__loading">
            <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
            <span>Loading agreement…</span>
        </div>
        <template v-else>
            <header class="agreement-detail__header">
                <div>
                    <p class="agreement-detail__eyebrow">Agreement #{{ agreement.id }}</p>
                    <h4 class="agreement-detail__title">{{ form.name || agreement.name || 'Untitled' }}</h4>
                    <p v-if="agreement.distributor" class="agreement-detail__meta mb-0">
                        {{ agreement.distributor.title }}
                        <span v-if="agreement.distributor.mainUser">· {{ agreement.distributor.mainUser.email }}</span>
                    </p>
                </div>
                <router-link class="btn btn-sm btn-outline-secondary" to="/distributor2/agreements">
                    ← All agreements
                </router-link>
            </header>

            <ul class="nav nav-tabs agreement-detail__tabs mb-3">
                <li class="nav-item">
                    <button type="button" class="nav-link" :class="{ active: mode === 'main' }" @click="mode = 'main'">
                        Main
                    </button>
                </li>
                <li class="nav-item">
                    <button type="button" class="nav-link" :class="{ active: mode === 'users' }" @click="mode = 'users'">
                        Users
                    </button>
                </li>
                <li class="nav-item">
                    <button type="button" class="nav-link" :class="{ active: mode === 'limits' }" @click="mode = 'limits'">
                        Limits
                    </button>
                </li>
            </ul>

            <div v-if="mode === 'main'" class="agreement-detail__panel">
                <div class="row g-3 mb-4">
                    <div class="col-md-3">
                        <label class="form-label">Title</label>
                        <input class="form-control" type="text" v-model="form.name">
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Type</label>
                        <select class="form-select" v-model="form.type">
                            <option value="com">commercial</option>
                            <option value="svc">service</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Region</label>
                        <select class="form-select" v-model="form.region">
                            <option value="global">Global</option>
                            <option value="CN">China</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">Valid Till</label>
                        <input class="form-control" type="date" v-model="form.validTill">
                    </div>
                </div>

                <div class="mb-3">
                    <rates-table :rates="form.rates"></rates-table>
                </div>
                <div class="d-flex gap-2 mb-3">
                    <button class="btn btn-primary" type="button" @click.once="submit()"
                        :disabled="!isSubmittable">Save changes</button>
                    <button v-if="isChanged" class="btn btn-outline-warning" type="button"
                        @click.once="cancel()">Undo</button>
                </div>
                <div v-if="errors" class="alert alert-warning alert-dismissible fade show" role="alert">
                    {{ errors }}
                    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
            </div>
            <div v-else-if="mode === 'users'">
                <agreement-users></agreement-users>
            </div>
            <div v-else-if="mode === 'limits'" class="agreement-detail__panel">
                <agreement-limits :agreementId="agreement.id" />
            </div>
        </template>
    </div>
</template>

<script>
import myAxios from '@/services/myAxios';
import moment from 'moment';
import RatesTable from './rates/index.vue';
import AgreementUsers from './users/index.vue'
import AgreementLimits from './Limits/index.vue'
import { eventBus } from '@/main'

export default {
    components: {
        RatesTable,
        AgreementUsers,
        AgreementLimits
    },
    data() {
        return {
            initialForm: {
                validTill: null,
                type: "com",
                rates: {},
                name: "",
                region: "global"
            },
            form: {
                validTill: null,
                type: "com",
                rates: {},
                name: "",
                region: null
            },
            agreement: {},
            errors: null,
            fetching: false,
            mode: 'users'
        }
    },
    computed: {
        isSubmittable() {
            return this.form.validTill
        },
        isChanged() {
            return JSON.stringify(this.form) !== JSON.stringify(this.initialForm)
        }
    },
    mounted() {
        this.fetchAgreement()
        eventBus.$on("ratesChangeComplete", (rates) => { this.form.rates = rates })
    },
    methods: {
        fetchAgreement() {
            this.fetching = true
            myAxios.get("/api/agreement?id=" + this.$route.params.id).then(resp => {
                this.agreement = resp.data.agreement
                this.form.name = this.agreement.name
                this.form.validTill = moment(this.agreement.validTill).format("YYYY-MM-DD");
                this.form.type = this.agreement.type
                this.form.region = this.agreement.region ?? "global"
                this.form.rates = resp.data.rates
                this.initialForm = JSON.parse(JSON.stringify(this.form))
                this.fetching = false
            })
        },
        cancel() {
            this.fetchAgreement()
        },
        submit() {
            var agreement = {
                id: this.agreement.id,
                ...this.form
            }
            this.fetching = true
            myAxios.post("/api/agreement/update", agreement)
                .then(() => {
                    this.$router.push("/distributor2/agreements");
                })
                .catch(err => {
                    this.fetching = false
                    this.errors = err.response.data
                })
        }
    }
}
</script>

<style scoped>
.agreement-detail {
    max-width: 1200px;
}

.agreement-detail__loading {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #6c757d;
    padding: 1.5rem 0;
}

.agreement-detail__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
}

.agreement-detail__eyebrow {
    margin: 0 0 0.15rem;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6c757d;
}

.agreement-detail__title {
    margin: 0 0 0.25rem;
    font-weight: 600;
}

.agreement-detail__meta {
    color: #6c757d;
    font-size: 0.9rem;
}

.agreement-detail__tabs .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    color: #6c757d;
    padding: 0.65rem 1rem;
    background: transparent;
}

.agreement-detail__tabs .nav-link:hover {
    color: #212529;
    border-bottom-color: #dee2e6;
}

.agreement-detail__tabs .nav-link.active {
    color: #0d6efd;
    border-bottom-color: #0d6efd;
    background: transparent;
    font-weight: 600;
}

.agreement-detail__panel {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1.25rem;
}
</style>
